import { CommercialApiConfig } from '@/services/CommercialApiConfig';
import { useFetch } from '@/composables/UseFetch';
import type {
	PagedUnderwriterResponse,
	ProvisionUnderwriterRequest,
	ProvisionUnderwriterResponse,
} from '@/models/UnderwriterModels';

type GetFn = <T>(url: string) => Promise<T>;
type SendFn = <T>(method: string, url: string, body?: unknown) => Promise<T>;

export function useUnderwriterApi(getImpl?: GetFn, sendImpl?: SendFn) {
	const base = () => `${CommercialApiConfig.endpoint}/underwriter`;
	const { fetchResilientWithAuth } = useFetch();

	const defaultGet: GetFn = async <T>(url: string) => {
		const response = await fetchResilientWithAuth(url, { method: 'GET' });
		if (!response.ok) {
			const body = await response.json().catch(() => ({})) as { error?: string };
			throw new Error(body.error ?? `GET ${url} failed: ${response.status} ${response.statusText}`);
		}
		return (await response.json()) as T;
	};

	const defaultSend: SendFn = async <T>(method: string, url: string, body?: unknown) => {
		const response = await fetchResilientWithAuth(url, {
			method,
			headers: { 'Content-Type': 'application/json' },
			body: body !== undefined ? JSON.stringify(body) : undefined,
		});
		if (!response.ok) {
			const errBody = await response.json().catch(() => ({})) as { error?: string };
			const message = errBody.error ?? `${method} ${url} failed: ${response.status} ${response.statusText}`;
			const err = Object.assign(new Error(message), { httpStatus: response.status });
			throw err;
		}
		return (await response.json()) as T;
	};

	const get: GetFn = <T>(url: string) => ((getImpl ?? defaultGet) as GetFn)<T>(url);
	const send: SendFn = <T>(method: string, url: string, body?: unknown) =>
		((sendImpl ?? defaultSend) as SendFn)<T>(method, url, body);

	function getUnderwriters(page: number, pageSize: number, includeExpired = false, lastName?: string): Promise<PagedUnderwriterResponse> {
		const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize), includeExpired: String(includeExpired) });
		if (lastName?.trim()) params.set('lastName', lastName.trim());
		return get<PagedUnderwriterResponse>(`${base()}?${params}`);
	}

	function provisionUnderwriter(request: ProvisionUnderwriterRequest): Promise<ProvisionUnderwriterResponse> {
		return send<ProvisionUnderwriterResponse>('POST', `${base()}/provision`, request);
	}

	function expireUnderwriter(profile: string): Promise<{ message: string }> {
		return send<{ message: string }>('POST', `${base()}/${encodeURIComponent(profile)}/expire`);
	}

	return { getUnderwriters, provisionUnderwriter, expireUnderwriter };
}
